<template>
  <main class="tc-login-root">
    <form
      class="tc-login-card"
      method="get"
      action="/api/auth/start"
      @submit="onSubmit"
    >
      <h1 class="tc-login-title">TimeCapture</h1>
      <p class="tc-login-tagline">Easy timesheets every time.</p>

      <div v-if="flash" role="alert" class="tc-login-flash" data-testid="tc-login-flash">
        {{ flash }}
      </div>

      <template v-if="typing">
        <!-- Placeholder rather than a visible label; aria-label carries the name
             for screen readers, which a placeholder alone would not. -->
        <input
          id="tc-login-org"
          ref="orgInput"
          v-model="org"
          type="text"
          name="org"
          required
          placeholder="Organization"
          aria-label="Organization"
          autocomplete="organization"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          class="form-control mb-3 tc-login-org-input"
          data-testid="tc-login-org-input"
        />

        <button
          type="submit"
          data-testid="tc-sign-in-vp"
          class="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 tc-btn-touch"
          :disabled="!org.trim()"
        >
          <svg class="tc-login-vp-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 2v20h4v-4h4v4h4V2H6zm2 14v-2h2v2H8zm0-4v-2h2v2H8zm0-4V6h2v2H8zm4 8v-2h2v2h-2zm0-4v-2h2v2h-2zm0-4V6h2v2h-2z"/>
          </svg>
          Sign in with Vantagepoint
        </button>

        <!-- Only reachable when there is a list to return to. -->
        <div v-if="remembered.length" class="tc-org-actions">
          <button
            type="button"
            class="tc-org-action"
            data-testid="tc-login-org-back"
            @click="showList"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M15 5l-7 7 7 7"/>
            </svg>
            Back
          </button>
        </div>
      </template>

      <template v-else>
        <ul class="tc-org-list" data-testid="tc-login-org-list">
          <li v-for="o in remembered" :key="o.id" class="tc-org-item">
            <!-- A submit button carrying name/value: the browser sends ?org=<id>
                 natively, so sign-in needs no JavaScript on this path. -->
            <button
              type="submit"
              name="org"
              :value="o.id"
              class="tc-org-row"
              :class="{ 'is-inert': removing }"
              :tabindex="removing ? -1 : undefined"
              :data-testid="`tc-login-org-${o.id}`"
            >
              <span class="tc-org-avatar" aria-hidden="true">{{ initials(o.name) }}</span>
              <span class="tc-org-name">{{ o.name }}</span>
              <svg v-if="!removing" class="tc-org-trail" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 5l7 7-7 7"/>
              </svg>
            </button>

            <button
              v-if="removing"
              type="button"
              class="tc-org-forget"
              :aria-label="`Forget ${o.name}`"
              :data-testid="`tc-login-org-forget-${o.id}`"
              @click="forget(o.id)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M6 6l12 12"/><path d="M18 6L6 18"/>
              </svg>
            </button>
          </li>
        </ul>

        <div class="tc-org-actions">
          <button
            type="button"
            class="tc-org-action"
            title="Sign in to a different organization"
            data-testid="tc-login-org-add"
            @click="useDifferent"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true">
              <path d="M12 5v14"/><path d="M5 12h14"/>
            </svg>
            Add
          </button>

          <button
            type="button"
            class="tc-org-action tc-org-action--quiet"
            :aria-pressed="removing"
            data-testid="tc-login-org-remove"
            @click="removing = !removing"
          >
            {{ removing ? 'Done' : 'Remove' }}
          </button>
        </div>
      </template>

      <p class="tc-login-fsp-credit">Provided by Full Sail Partners</p>
    </form>
  </main>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { listSignedInOrgs, forgetSignedInOrg, stashPendingOrg } from '../auth/signedInOrgs';

const route = useRoute();
const router = useRouter();

const orgInput = ref<HTMLInputElement | null>(null);
const deeplinkOrg = typeof route.query.org === 'string' ? route.query.org : '';
const remembered = ref(listSignedInOrgs());

// The free-text field is the only path with nothing remembered, and a deeplink
// names an org this browser may never have used.
const typing = ref(remembered.value.length === 0 || deeplinkOrg !== '');
const removing = ref(false);

const org = ref(deeplinkOrg);
const flash = ref(typeof route.query.flash === 'string' ? route.query.flash : '');

// Punctuation is not a word, so "FSP Dev (Local)" yields FL rather than "F(".
function initials(name: string): string {
  const words = name.match(/[A-Za-z0-9]+/g) ?? [];
  const first = words[0] ?? '';
  if (!first) return '?';
  const last = words[words.length - 1] ?? first;
  return (words.length === 1 ? first.slice(0, 2) : first.charAt(0) + last.charAt(0)).toUpperCase();
}

// Add: an empty field, focused, because pressing Add means you intend to type.
// Remove mode ends here — Back must not return you to a card mid-edit.
function useDifferent(): void {
  typing.value = true;
  removing.value = false;
  org.value = '';
  void nextTick(() => orgInput.value?.focus());
}

// The field is not cleared here: every route back into it goes through
// useDifferent, which opens it empty.
function showList(): void {
  typing.value = false;
}

function forget(id: string): void {
  forgetSignedInOrg(id);
  remembered.value = listSignedInOrgs();
  // Nothing left to list, so the field is the only way on and Back has no target.
  if (remembered.value.length === 0) useDifferent();
}

// The page unloads into VP from here, so the chosen org is stashed and promoted
// to the remembered list only once the browser lands back authenticated
// (ADR-0023 Amendment D). A failed sign-in returns to /login and writes nothing.
//
// Row buttons carry name="org"; the free-text CTA does not, and neither does
// pressing Enter in the field, so both fall through to the typed value.
function onSubmit(e: Event): void {
  const picked = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  const chosen = picked?.name === 'org' ? picked.value : org.value.trim();
  stashPendingOrg(chosen);
}

// Strip ?org=&flash= from the login URL after first render. ADR-0023 §1:
// the deeplink is a routing hint for prefill, not a credential.
onMounted(() => {
  if (route.query.org || route.query.flash) {
    router.replace({ path: route.path, query: {} });
  }
});
</script>
